import React from 'react';
import { ArrowRight, Play } from 'lucide-react';
import { aboutImages, teamPhoto, business } from '../mock';
import useReveal from '../hooks/useReveal';

export default function About() {
  return (
    <section id="about" className="section">
      <div className="container-x">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div className="reveal">
            <p className="eyebrow mb-2">About Us</p>
            <h2 style={{ fontSize: 'clamp(2rem, 4vw, 3.1rem)' }}>
              Care and connection that feels like family
            </h2>
            <p className="mt-6" style={{ color: 'var(--muted)' }}>
              Mom&rsquo;s Best Friend Senior Care began when our founders couldn&rsquo;t find the kind of
              care they wanted for a dear senior family member. They wanted more than a sitter &mdash;
              someone who would feel like his best friend and truly attend to his happiness and well-being.
            </p>
            <p className="mt-4" style={{ color: 'var(--muted)' }}>
              With nearly a decade of experience in in-home care, they built a women-owned company
              across Dallas and Fort Worth devoted to a level of senior home care you simply
              couldn&rsquo;t find anywhere else.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-10">
              {[
                { n: `${business.since}`, l: 'Serving DFW Since' },
                { n: business.familiesServed, l: 'Families Cared For' },
                { n: '10-Step', l: 'Caregiver Screening' },
              ].map((s) => (
                <div
                  key={s.l}
                  className="rounded-2xl p-5 flex items-center gap-4 sm:block"
                  style={{ backgroundColor: 'var(--sage-soft)', border: '1px solid var(--line)' }}
                >
                  <div className="font-serif-display text-3xl md:text-[2.4rem] leading-none shrink-0" style={{ color: 'var(--forest)' }}>{s.n}</div>
                  <div className="text-sm sm:mt-2 font-medium" style={{ color: 'var(--muted)' }}>{s.l}</div>
                </div>
              ))}
            </div>

            <a href="#services" className="btn-outline mt-10">
              Explore Care Options <ArrowRight size={18} />
            </a>
          </div>

          <div className="reveal relative">
            <div className="grid grid-cols-2 gap-4">
              <div className="arch-top" style={{ aspectRatio: '3/4' }}>
                <img src={aboutImages.primary} alt="Caregiver enjoying an activity with an older adult" className="w-full h-full object-cover" />
              </div>
              <div className="rounded-card mt-10" style={{ aspectRatio: '3/4' }}>
                <img src={aboutImages.secondary} alt="An active senior woman in a bright kitchen at home" className="w-full h-full object-cover" />
              </div>
            </div>
            <div
              className="absolute -bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-3 px-5 py-3 rounded-full shadow-lg"
              style={{ backgroundColor: 'var(--forest)' }}
            >
              <span className="flex items-center justify-center rounded-full" style={{ width: 34, height: 34, backgroundColor: 'var(--sage)' }}>
                <Play size={16} style={{ color: 'var(--forest)' }} fill="currentColor" />
              </span>
              <span className="text-white text-sm font-medium pr-1">A promise of dignity &amp; independence</span>
            </div>
          </div>
        </div>

        {/* Real owner + care team photo */}
        <div className="reveal mt-20" data-testid="team-photo-block">
          <div className="relative rounded-[28px] overflow-hidden" style={{ boxShadow: '0 30px 70px -34px rgba(30,59,44,0.5)' }}>
            <img
              src={teamPhoto}
              alt="The owners and care team of Mom's Best Friend Senior Care in Dallas–Fort Worth"
              className="w-full object-cover"
              style={{ maxHeight: 640 }}
            />
            <div className="absolute inset-x-0 bottom-0" style={{ background: 'linear-gradient(180deg, rgba(20,42,31,0) 0%, rgba(20,42,31,0.85) 100%)' }}>
              <div className="p-7 sm:p-10">
                <p className="eyebrow" style={{ color: '#B8D89E' }}>Our Family, Serving Yours</p>
                <h3 className="text-white text-2xl sm:text-3xl mt-1" style={{ color: '#fff' }}>
                  Meet the owners &amp; care team behind Mom&rsquo;s Best Friend
                </h3>
                <p className="text-white/75 text-[15px] mt-2 max-w-[640px]">
                  A women-owned team of dedicated caregivers and care managers, proudly caring for
                  Dallas&ndash;Fort Worth families since {business.since}.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
